import { ColDef, IRowNode } from "ag-grid-enterprise";
import Enumerable from "linq";
import { useCallback, useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { isHotkeyPressed, useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../../common/utilities/GetObjectTypeName";
import { StepMenuStepProps } from "../../../../components/step-menu/StepMenu";
import { useRouteContext } from "../../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useObjectTagValuesAvailableItems } from "../../../../queries/object-tag-values/UseObjectTagValuesAvailableitems";
import CheckboxCellRenderer from "../../../ag-grid-extensions/renderers/CheckboxCellRenderer";
import { ContentContainer } from "../../../content-container/ContentContainer";
import { ContentSection } from "../../../content-section/ContentSection";
import DefaultGrid, { DefaultGridRef } from "../../../default-grid/DefaultGrid";
import SideSheetSwitch from "../../../side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../../side-sheet/side-sheet/SideSheet";
import TabBar from "../../../tab-bar/TabBar";
import TabContainer from "../../../tab-container/TabContainer";
import TabWithCount from "../../../tab-with-count/TabWithCount";
import { ObjectTagValuesConfigurationFieldType, ObjectTagValuesTagGroupConfigurationFieldType, ObjectTagValuesTagGroupTagConfigurationFieldType, TagGroupFieldKeyType } from "../ObjectTagValuesConfigurationFieldTypes";
import { useSelectTagsStepState } from "./SelectTagStepState";
import { createTagField, createTagGridColumns, createTagGridRows, filterTagGridRows, ObjectTagValuesTagGridRow } from "./SelectTagsStepUtils";

export type SelectTagsStepProps = {
    tagGroupKey: TagGroupFieldKeyType;
} & StepMenuStepProps;

export default function SelectTagsStep({
    tagGroupKey
}: SelectTagsStepProps) {

    const gridRef = useRef<DefaultGridRef>(null);
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const {
        state: {
            viewSelected,
            viewAll
        },
        setViewSelected,
        setViewAll
    } = useSelectTagsStepState();

    const { data: availableItems/*, status*/ } = useQueryNotifications(useObjectTagValuesAvailableItems)({
        groupID: routeContext.groupID!,
        objectType: routeContext.contextObject?.objectType!,
        objectID: routeContext.contextObject?.id!
    });

    const {
        getValues
    } = useFormContext<ObjectTagValuesConfigurationFieldType>();

    const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(0);

    const tagGroups = getValues("tagGroups");

    const assignedGroups = useMemo(() => Enumerable
        .from<ObjectTagValuesTagGroupConfigurationFieldType>(tagGroups)
        .selectMany<ObjectTagValuesTagGroupTagConfigurationFieldType, { equatableKey: string, groupName: string }>(
            g => g.tags,
            (o, i) => ({
                equatableKey: i.equatableKey,
                groupName: o.name
            })
        )
        .toLookup(g => g.equatableKey, t => t.groupName), [
        tagGroups
    ]);

    const {
        fields: tagFields,
        replace
    } = useFieldArray<ObjectTagValuesConfigurationFieldType, `${TagGroupFieldKeyType}.tags`, "fieldID">({
        name: `${tagGroupKey}.tags`,
        keyName: "fieldID"
    });

    const availableTagGridRows = useMemo(() => {
        return createTagGridRows(
            availableItems,
            tagFields,
            assignedGroups
        );
    }, [
        assignedGroups,
        availableItems,
        tagFields
    ]);

    const tagCount = availableTagGridRows.filter(g => g.checked).length;

    const gridRows = useMemo(() => {
        return filterTagGridRows(
            availableTagGridRows,
            viewSelected,
            viewAll
        );
    }, [
        availableTagGridRows,
        viewSelected,
        viewAll
    ]);

    const onItemsClick = useCallback((items: Array<ObjectTagValuesTagGridRow>) => {
        const checkedItems = items.filter(i => i.checked).map(i => createTagField(i));
        const uncheckedItems = items.filter(i => !i.checked).map(i => i.equatableKey);
        const updated = [
            ...tagFields.filter(o => !uncheckedItems.includes(o.equatableKey)),
            ...checkedItems
        ];

        replace(updated);
    }, [
        tagFields,
        replace
    ]);

    const getFilteredGridRows = useCallback((): Array<ObjectTagValuesTagGridRow> => {
        const filteredGridRows = new Array<ObjectTagValuesTagGridRow>();
        gridRef.current?.api.forEachNodeAfterFilter((node: IRowNode<ObjectTagValuesTagGridRow>) => {
            const row = node.data;
            if (row) {
                filteredGridRows.push(row);
            }
        });
        return filteredGridRows;
    }, []);

    useHotkeys(
        ["ctrl+a", "ctrl+u"],
        (keyboardEvent: KeyboardEvent, _) => {
            const filteredGridRows = getFilteredGridRows();
            const range = filteredGridRows.map(i => ({ ...i, checked: keyboardEvent.key === "a" }));
            onItemsClick(range);
        }, {
        enabled: true,
        preventDefault: true
    });

    const gridColumns: Array<ColDef> = useMemo(() => {
        function onCheckboxChange(item: ObjectTagValuesTagGridRow, checked: boolean) {
            const filteredGridRows = getFilteredGridRows();
            const isShiftKeyDown = isHotkeyPressed("shift");
            const index = filteredGridRows.findIndex(i => i.equatableKey === item.equatableKey);

            if (isShiftKeyDown) {
                const minIndex = Math.min(lastSelectedIndex, index);
                const maxIndex = Math.max(lastSelectedIndex, index) + 1;
                const checkState = filteredGridRows[lastSelectedIndex].checked;
                const range = filteredGridRows.slice(minIndex, maxIndex);

                onItemsClick(range.map(i => ({ ...i, checked: checkState })));
            } else {
                if (index > -1) {
                    onItemsClick([{ ...item, checked: !item.checked }]);
                    setLastSelectedIndex(index);
                }
            }
        }

        return createTagGridColumns(onCheckboxChange);
    }, [
        lastSelectedIndex,
        onItemsClick,
        getFilteredGridRows
    ]);

    return (
        <>
            <TabBar value={0}>
                <TabWithCount label="Tag Values" count={tagCount} />
            </TabBar>
            <TabContainer visible={true}>
                <ContentContainer>
                    <ContentSection>
                        <DefaultGrid
                            ref={gridRef}
                            columnDefs={gridColumns}
                            rowData={gridRows}
                            getRowId={row => row.data.equatableKey}
                            components={{
                                checkBoxCellRenderer: CheckboxCellRenderer
                            }}
                            onFilterChanged={_event => {
                                setLastSelectedIndex(0);
                            }}
                        />
                    </ContentSection>
                    <SideSheet
                        title={t("options")}
                        open={true}
                    >
                        <SideSheetSwitch
                            label={t("objectTagValuesConfiguration.steps.selectTags.viewSelected", {
                                pluralObjectTypeName: getObjectTypeName(eLynxObjectTypeEnum.Tag, true)
                            })}
                            checked={viewSelected}
                            onChange={(checked: boolean) => {
                                setViewSelected(checked);
                            }}
                        />

                        <SideSheetSwitch
                            label={t("objectTagValuesConfiguration.steps.selectTags.viewAll", {
                                pluralObjectTypeName: getObjectTypeName(eLynxObjectTypeEnum.Tag, true)
                            })}
                            checked={viewAll}
                            onChange={(checked: boolean) => {
                                setViewAll(checked);
                            }}
                        />
                    </SideSheet>
                </ContentContainer>
            </TabContainer>
        </>
    );
}